import {
    Round,
    RoundReceipt,
    Class,
    Judge,
    Judging,
    Floor,
    PanelJudge,
    RoundType,
    JudgingType
} from '@vote4dance/judging-schema';
import {
    ClassId,
    CompetitionId,
    HeatsTeamId,
    JudgingSettings,
    RoundGroupPrioritySettings,
    RoundId,
    RoundPriorityType,
    ScheduleId,
    SkatingEntry,
    UserId,
    PanelId
} from '@vote4dance/judging-types';
import { StreamSource } from "../hooks/streams";
import { JudgingItem } from './judging';

export type RoundItem = Round
& Pick<Class, 'class_label' | 'class_name' | 'class_order' | 'class_group_id' | 'class_role'>
& Pick<Judge, 'judge_id'>
& Pick<Floor, 'floor_name' | 'floor_code'>
& Pick<Judging, 'judging_label' | 'judging_type' | 'judging_components' | 'judging_settings'>
& Pick<PanelJudge, 'judge_type'>
& {
    round_overrun_seconds: string,
    round_estimated_time: string,
    round_children: number,
    round_parent_label: string,
    round_judges_done: number,
    round_heats_done: number,
    round_count: number,
    round_new_teams: number,
    acro_name: string,
    round_receipt_heats: string | null,
    class_group_count: number,
    class_role_count: number
}
& {
    default_panel_id?: PanelId | null,
    panel_id?: PanelId | null
}
& {
    isPlaceholder?: boolean,
    rowKey?: string,
    children?: RoundItem[],
    isGroupedInSchedule?: boolean
}

export type RoundReceiptItem = RoundReceipt;

type RoundItemCompanion = {
    isParentRound(round: RoundItem): boolean;
    isChildRound(round: RoundItem): boolean;
    isSubRound(round: RoundItem): boolean;
    isFinalRound(round: RoundItem): boolean;
    isLastRoundInClass(round: RoundItem, classRounds: RoundItem[] | undefined): boolean;
    isSkating(round: RoundItem): boolean;
    isOverride(round: RoundItem): boolean;
    isShared(round: RoundItem): boolean;
    isPointSummary(round: RoundItem): boolean;
    leftLabel(round: RoundItem): string;
    getClassLabel(round: RoundItem): string;
    getRoundLabel(round: RoundItem): string;
    getFullName(round: RoundItem): string;
    getShortLetters(round: RoundItem): string;
    usesSkating(round: RoundItem): boolean;
    usesPlacement(round: RoundItem): boolean;
    usesSum(round: RoundItem): boolean;
    usesPriority(round: RoundItem): boolean;
    usesPointSummary(round: RoundItem): boolean;
    usesStars(round: RoundItem): boolean;
    shouldShowMarks(round: RoundItem, placement: number, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean;
    shouldShowPlacement(round: RoundItem, placement: number, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean;
    shouldShowPartial(round: RoundItem, placement: number, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean;
    filteredCoach(round: RoundItem): boolean;
    isCoachingTeam(round: RoundItem, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean;
    getSkatingData(round: RoundItem): SkatingEntry[],
    getSubmitValidation(round: RoundItem): null | [any,any];
    getPrioritySettings(round: RoundItem): RoundGroupPrioritySettings;
    getJudgingSettings(round: RoundItem): JudgingSettings;
}

export const RoundItem: RoundItemCompanion = {
    isParentRound(round: RoundItem): boolean {
        return round.children ? round.children.filter(a => a.round_type === RoundType.Normal).length > 0 : round.round_children > 0;
    },

    isChildRound(round: RoundItem): boolean {
        return parseInt(round.round_parent_id) > 0;
    },

    isSubRound(round: RoundItem): boolean {
        return parseInt(round.round_parent_id) > 0 && round.round_type === RoundType.Normal;
    },

    isFinalRound(round: RoundItem): boolean {
        return round.round_num_through == 0;
    },

    isLastRoundInClass(round: RoundItem, classRounds: RoundItem[] | undefined): boolean {
        const classRoundIds = classRounds ? classRounds.filter(a => a.round_type === RoundType.Normal).map(a => a.round_id) : [];
        return classRoundIds.indexOf(round.round_id) === classRoundIds.length-1;
    },

    isSkating(round: RoundItem): boolean {
        return round.round_type === 'skating';
    },

    isOverride(round: RoundItem): boolean {
        return round.round_type === 'override';
    },

    isShared(round: RoundItem): boolean {
        const judgingType = round.judging_type;
        return (round.round_children > 0) && judgingType != 'full' && judgingType != 'partial';
    },

    isPointSummary(round: RoundItem): boolean {
        return round.round_type === 'points';
    },

    leftLabel(round: RoundItem): string {
        return (round.round_beat || '') + (round.floor_name || '')
    },

    getClassLabel(round: RoundItem): string {
        return round.class_label ? round.class_label : (round.class_name || '')
    },

    getRoundLabel(round: RoundItem): string {
        /*
        if(round.children) {
            return round.children.map((each: Round) => {
                return each.round_label
            }).join(', ')
        }
        */
        return round.round_label || '';
    },

    getFullName(round: RoundItem): string {
        return this.getClassLabel(round) + ' - ' + this.getRoundLabel(round)
    },

    getShortLetters(round: RoundItem): string {
        let label = round.round_label!;

        // Check if we have parentheses and remove them
        if(label.indexOf("(") != -1) {
            label = label.substring(0, label.indexOf("("));
        }

        return label.replace(/\S[^\s^\d]*/g, function (text) {
            return text.charAt(0);
        }).replace(/\s/g, '').toUpperCase();
    },

    usesSkating(round: RoundItem): boolean {
        const disableSkating = JudgingItem.getJudgingSettings(round as unknown as JudgingItem)?.disableSkating;
        return !this.usesStars(round) && !this.isPointSummary(round) && (
            disableSkating ? 
                disableSkating != true 
            : true
        );
    },

    usesPlacement(round: RoundItem): boolean {
        const disablePlacement = JudgingItem.getJudgingSettings(round as unknown as JudgingItem)?.disablePlacement;
        return !this.usesStars(round) && !this.isPointSummary(round) && (
            disablePlacement ? 
                disablePlacement != true 
            : true
        );
    },

    usesSum(round: RoundItem): boolean {
        if (this.isSkating(round) || this.isOverride(round)) {
            return false;
        }

        if(this.usesPriority(round) && this.usesPointSummary(round)) {
            return true;
        }

        if(!this.usesSkating(round)) {
            return true;
        }

        const judgingType = round.judging_type;
        switch (judgingType) {
            case JudgingType.Select: 
            case JudgingType.Match:
            case JudgingType.Points: 
            case JudgingType.OneToFive: 
            case JudgingType.Ajs: 
            case JudgingType.CollectStars:
                return true;
            case JudgingType.Placement: return this.isShared(round);
            default: return false;
        }
    },

    usesPriority(round: RoundItem): boolean {
        const round_priority_settings = round.round_priority_settings;
        return !!round_priority_settings;
    },

    usesPointSummary(round: RoundItem): boolean {
        const round_priority_settings = this.getPrioritySettings(round);
        return round_priority_settings.type == RoundPriorityType.Groups;
    },

    usesStars(round: RoundItem): boolean {
        return round.judging_type === JudgingType.CollectStars;
    },

    shouldShowMarks(round: RoundItem, placement: number, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean {
        const hidePosition = round.round_hide_results_pos;
        const hasFilter = hidePosition != null && hidePosition >= 0;
        return !hasFilter || (
            hidePosition > placement ? true : 
                !['marks','all','coach'].includes(round.round_hide_results_type) ||
                this.isCoachingTeam(round, team, filteredCoachTeams)
        )
    },

    shouldShowPlacement(round: RoundItem, placement: number, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean {
        const hidePosition = round.round_hide_results_pos;
        const hasFilter = hidePosition != null && hidePosition >= 0;
        return !hasFilter || (
            hidePosition > placement ? true : 
                !['placements','all','coach'].includes(round.round_hide_results_type) ||
                this.isCoachingTeam(round, team, filteredCoachTeams)
        )
    },

    shouldShowPartial(round: RoundItem, placement: number, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean {
        const hidePosition = round.round_hide_results_pos;
        const hasFilter = hidePosition != null && hidePosition >= 0;
        return !hasFilter || (
            hidePosition > placement ? true : 
                !['partial','all','coach'].includes(round.round_hide_results_type) ||
                this.isCoachingTeam(round, team, filteredCoachTeams)
        )
    },

    filteredCoach(round: RoundItem): boolean {
        return round.round_hide_results_type == 'coach';
    },

    isCoachingTeam(round: RoundItem, team?: HeatsTeamId, filteredCoachTeams?: HeatsTeamId[]): boolean {
        return this.filteredCoach(round) && filteredCoachTeams && team ? filteredCoachTeams.includes(team) : false;
    },

    getSkatingData(round: RoundItem): SkatingEntry[] {
        // TODO: Data can be parsed already from class-rounds query, but not schedule-rounds
        let skatingDataInput: any;
        try {
            skatingDataInput = JSON.parse(round.round_skating!);
        } catch (e) {
            skatingDataInput = round.round_skating;
        }
        return skatingDataInput ? Array.from(skatingDataInput).map((skate: any) => (
            skate as SkatingEntry
        )) : [];
    },

    getSubmitValidation(round: RoundItem): null | [any, any] {
        const settings = round.judging_settings && JSON.parse(round.judging_settings) || {};
        if(settings.validation) {
            switch (settings.validation) {
                case 'abcd': case 'abcdx': {
                    const round_num_through = round.round_num_through;
                    const count = round_num_through >= 16 ? 4 : round_num_through >= 8 ? 2 : 1;
                    return settings.entries.map((point:string) => [point, point === 'X' ? 2 : count]);
                }
                case 'absolute': {
                    const submitValidation = JSON.parse(round.round_submit_validation!);
                    if(submitValidation != null) {
                        return settings.entries.map((point:string) => [point, submitValidation.absolute[point]]);
                    }
                    return settings.entries.map((point:string) => [point, null]);
                }
                default: return settings.entries.map((point:string) => [point, null]);
            }
        } else {
            return null;
        }
    },

    getPrioritySettings(round: RoundItem): RoundGroupPrioritySettings {
        const round_priority_settings = round.round_priority_settings;
        return round_priority_settings && JSON.parse(round_priority_settings);
    },

    getJudgingSettings(judging: RoundItem): JudgingSettings {
		const settings = judging.judging_settings ? JSON.parse(judging.judging_settings) : {};
		return settings;
	}
}

export function ScheduleRoundSource(competition_id: CompetitionId, schedule_id: ScheduleId) {
    return new StreamSource<RoundItem[]>(`/comp/${competition_id}/schedule/${schedule_id}/rounds`);
}

export function ClassRoundSource(class_id: ClassId) {
    return new StreamSource<RoundItem[]>(`/class-rounds/${class_id}`);
}

export function OpenRoundSource(competition_id: CompetitionId, user_id: UserId) {
    return new StreamSource<RoundItem[]>(`/openround/${competition_id}/${user_id}`);
}

export function RoundReceiptSource(round_id: RoundId) {
    return new StreamSource<RoundReceiptItem[]>(`/roundreceipt/${round_id}`);
}

export function CompetitionRoundSource(competition_id: CompetitionId) {
    return new StreamSource<RoundItem[]>(`/rounds/${competition_id}`);
}

export function ActiveRoundsSource(competition_id: CompetitionId) {
    return new StreamSource<RoundItem[]>(`/active-rounds/${competition_id}`);
}

export function RoundPathSource(competition_id: CompetitionId, round_id: RoundId) {
    return new StreamSource<RoundItem[]>(`/comp/${competition_id}/rounds/${round_id}/path`);
}
